import React, { useState } from "react";
import { Card, Button, Spin, Avatar, Tag, Empty } from "antd";
import EmployeeAvatar from "Common/EmployeeAvatar";
import { ReloadOutlined, ApartmentOutlined, UserOutlined } from "@ant-design/icons";
import { useHierarchy } from "Hooks/AdminHooks/Organization/useHieararchy";
import HierarchyNode from "./HierarhcyNode";
import "./HierarchyView.scss";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";

const HierarchyView = () => {
  const { hierarchy, loading, fetchDirectReports } = useHierarchy();
  const navigate = useNavigate();
  const [treeData, setTreeData] = useState(hierarchy);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [loadingNodes, setLoadingNodes] = useState({});

  React.useEffect(() => {
    setTreeData(hierarchy);
  }, [hierarchy]);

  // Function to find and update a node in the tree
  const findAndUpdateNode = (nodes, nodeId, updateFn) => {
    if (!nodes) return [];

    return nodes.map((node) => {
      if (node.id === nodeId) {
        return updateFn(node);
      }

      // Recursively check children
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: findAndUpdateNode(node.children, nodeId, updateFn),
        };
      }

      return node;
    });
  };

  const toggleNode = async (node) => {
    const nodeId = node.id;
    const isCurrentlyExpanded = expandedNodes[nodeId];

    // Toggle the expanded state
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !isCurrentlyExpanded,
    }));

    // If expanding and we don't have children yet, fetch them
    if (
      !isCurrentlyExpanded &&
      node.hasDirectReports &&
      (!node.children || node.children.length === 0)
    ) {
      // Set loading state for this specific node
      setLoadingNodes((prev) => ({ ...prev, [nodeId]: true }));

      try {
        const children = await fetchDirectReports(nodeId);

        // Update the tree data with the new children
        setTreeData((prev) =>
          findAndUpdateNode(prev, nodeId, (n) => ({
            ...n,
            children: children || [],
          }))
        );
      } catch (error) {
        console.error("Error fetching direct reports:", error);
      } finally {
        // Clear loading state for this node
        setLoadingNodes((prev) => ({ ...prev, [nodeId]: false }));
      }
    }
  };

  const collapseAll = () => {
    setExpandedNodes({});
  };

  const selectNode = (node) => {
    setSelectedNode(node);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="container-fluid py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold mb-1">Organization Hierarchy</h2>
            <p className="text-muted mb-0">View the organizational structure</p>
          </div>
          <div className="d-flex gap-2">
            <Button icon={<ReloadOutlined />} size="small">
              Refresh
            </Button>
            <Button icon={<ApartmentOutlined />} size="small" onClick={collapseAll}>
              Collapse All
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spin size="large" />
            <p className="text-muted mt-3">Loading hierarchy...</p>
          </div>
        ) : (
          <div className="hierarchy-stack-container">
            {/* 1. Organization Chart (Full Width) */}
            <div className="hierarchy-section mb-4">
              <Card className="hierarchy-scroll-wrapper" title="Organization Chart">
                <div className="hierarchy-tree-container">
                  {treeData.map((node) => (
                    <HierarchyNode
                      key={node.id}
                      node={node}
                      expandedNodes={expandedNodes}
                      toggleNode={toggleNode}
                      selectNode={selectNode}
                      selectedNode={selectedNode}
                      loadingNodes={loadingNodes}
                      isRoot={true}
                      level={0}
                    />
                  ))}
                </div>
              </Card>
            </div>

            {/* 2. Employee Details - */}
            <div className="details-section">
              <Card
                className="shadow-sm border-0"
                title={<span className="fw-bold text-dark">Employee Information</span>}
              >
                {selectedNode ? (
                  <div className="employee-details-horizontal px-3">
                    {/* Header with Profile Summary */}
                    {/* 3. Restructured to a flex container for alignment */}
                    <div className="details-main-info mb-4 border-bottom pb-4 d-flex justify-content-between align-items-center">
                      {/* Left side: Avatar and Employee Info */}
                      <div className="d-flex align-items-center">
                        <EmployeeAvatar
                          name={selectedNode.fullName}
                          profileImage={selectedNode.profileImage}
                          size={90}
                          className="shadow-sm border border-2 border-white"
                          style={{ fontSize: "28px" }}
                        />

                        <div style={{ marginLeft: "16px" }}>
                          <h3
                            className="fw-bold mb-1 text-dark"
                            style={{ letterSpacing: "-0.5px", marginBottom: "4px" }}
                          >
                            {selectedNode.fullName}
                          </h3>

                          <div className="d-flex gap-3">
                            <span className="text-muted small">
                              <i className="far fa-envelope me-1"></i>
                              {selectedNode.email}
                            </span>

                            <span className="text-muted small">
                              <i className="far fa-id-badge me-1"></i>
                              {selectedNode.publicId}
                            </span>
                          </div>
                        </div>
                      </div>

                      <Button
                        type="default"
                        icon={<UserOutlined />}
                        onClick={() => navigate(`/employeeDetail/${selectedNode.id}`)}
                      >
                        View Full Profile
                      </Button>
                    </div>

                    {/* Data Grid - Clean Typography Layout */}
                    <div className="row mt-4">
                      {[
                        { label: "Designation", value: selectedNode.designationName, icon: "⭐" },
                        { label: "Department", value: selectedNode.departmentName, icon: "🏢" },
                        { label: "Job Title", value: selectedNode.jobTitleName, icon: "💼" },
                        { label: "Supervisor ", value: selectedNode.supervisorName, icon: "👤" },
                        {
                          label: "Created On",
                          value: selectedNode.createdOn
                            ? new Date(selectedNode.createdOn).toLocaleDateString()
                            : "N/A",
                          icon: "📅",
                        },
                      ].map((item, idx) => (
                        <div className="col-md-4 mb-4" key={idx}>
                          <div className="details-info-group">
                            <label
                              className="text-uppercase small fw-bold text-muted mb-1 d-block"
                              style={{ letterSpacing: "1px" }}
                            >
                              {item.label}
                            </label>
                            <div className="d-flex align-items-center">
                              <span className="me-2 text-primary opacity-75">{item.icon}</span>
                              <span className="fw-medium text-dark">{item.value || "N/A"}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Select an employee from the chart to view full details"
                    />
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default HierarchyView;
